import React, { useEffect, useState } from "react";
import { Alert, Button, Card, CardContent, Stack, TextField, Typography } from "@mui/material";
import { useParams } from "react-router-dom";

import {
  addNetworkPortalMessage,
  getNetworkPortalMessageAttachment,
  getNetworkPortalOffer,
  markNetworkPortalMessagesRead,
  markNetworkPortalOfferViewed,
  respondNetworkPortalOffer,
  uploadNetworkPortalMessageAttachments,
} from "../../services/coreNetworkPortal";
import {
  NetworkPortalLayout,
  PortalAuthState,
  PortalLoading,
  StatusChip,
  formatCurrency,
  formatDateTime,
  safeArray,
  useNetworkPortalSession,
} from "./networkPortalShared";

export default function NetworkPortalOfferDetail() {
  const { id } = useParams();
  const { member, loading, error } = useNetworkPortalSession();
  const [detail, setDetail] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [busy, setBusy] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [responseNote, setResponseNote] = useState("");
  const [question, setQuestion] = useState("");
  const [proposedFeeAmount, setProposedFeeAmount] = useState("");
  const [proposedCompletionDate, setProposedCompletionDate] = useState("");
  const [messageSelectedFiles, setMessageSelectedFiles] = useState([]);
  const [uploadedMessageFiles, setUploadedMessageFiles] = useState([]);

  useEffect(() => {
    if (!member) return;
    let active = true;

    async function loadDetail() {
      try {
        setPageLoading(true);
        setPageError("");
        await markNetworkPortalOfferViewed(id);
        const response = await getNetworkPortalOffer(id);
        await markNetworkPortalMessagesRead(response?.workRequest?.id);
        if (active) {
          setDetail(response);
        }
      } catch (loadError) {
        if (active) {
          setPageError(loadError?.response?.data?.message || loadError.message || "Unable to load offer.");
        }
      } finally {
        if (active) {
          setPageLoading(false);
        }
      }
    }

    loadDetail();
    return () => {
      active = false;
    };
  }, [id, member]);

  if (loading || pageLoading) return <PortalLoading />;
  if (!member) return <PortalAuthState error={error} />;
  if (!detail) {
    return <NetworkPortalLayout title="Offer" member={member}><Alert severity="error">{pageError || "Offer not found."}</Alert></NetworkPortalLayout>;
  }

  const { offer, workRequest, messages = [], submissions = [] } = detail;
  const responsePayload = offer?.responsePayloadJson || {};
  const unreadMessageCount = Number(detail?.unreadMessageCount || 0);

  async function refresh() {
    const response = await getNetworkPortalOffer(id);
    setDetail(response);
    setUploadedMessageFiles([]);
    setMessageSelectedFiles([]);
  }

  async function runAction(actionKey, callback) {
    try {
      setBusy(actionKey);
      setPageError("");
      await callback();
      await refresh();
    } catch (actionError) {
      setPageError(actionError?.response?.data?.message || actionError.message || "Action failed.");
    } finally {
      setBusy("");
    }
  }

  async function openMessageAttachment(message, attachment) {
    const response = await getNetworkPortalMessageAttachment(message.id, attachment.fileId);
    const url = response?.attachment?.downloadUrl || response?.attachment?.previewUrl;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }

  return (
    <NetworkPortalLayout title={workRequest?.title || `Offer #${offer.id}`} subtitle="Offer detail" member={member}>
      <Stack spacing={3}>
        {pageError ? <Alert severity="error">{pageError}</Alert> : null}
        <Card elevation={0} sx={{ border: "1px solid #d9e2ec", borderRadius: 3 }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <StatusChip value={offer.status} />
                <StatusChip value={workRequest?.priority} />
                <StatusChip value={workRequest?.requestType} />
              </Stack>
              <Typography variant="body2">{workRequest?.description || workRequest?.instructions || "No instructions supplied."}</Typography>
              <Typography variant="body2" color="text.secondary">
                Offered fee {formatCurrency(offer?.quotedPrice, offer?.currency)} | Due {formatDateTime(workRequest?.deadlineAt)} | Expires {formatDateTime(offer?.expiresAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Ship to: {workRequest?.shipToJson?.city || "-"}, {workRequest?.shipToJson?.province || workRequest?.shipToJson?.state || "-"}</Typography>
              {responsePayload?.proposedFeeAmount || responsePayload?.proposedCompletionDate || responsePayload?.responseNote ? (
                <Alert severity="info">
                  Latest response: {responsePayload?.proposedFeeAmount ? `${formatCurrency(responsePayload.proposedFeeAmount, responsePayload.proposedFeeCurrency || offer?.currency)} ` : ""}
                  {responsePayload?.proposedCompletionDate ? `| Proposed completion ${formatDateTime(responsePayload.proposedCompletionDate)} ` : ""}
                  {responsePayload?.responseNote ? `| ${responsePayload.responseNote}` : ""}
                </Alert>
              ) : null}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  disabled={busy === "accept"}
                  onClick={() =>
                    runAction("accept", () =>
                      respondNetworkPortalOffer(offer.id, {
                        responseType: "accept",
                        responseNote: responseNote || undefined,
                        question: question || undefined,
                        proposedFeeAmount: proposedFeeAmount || undefined,
                        proposedFeeCurrency: offer?.currency || "CAD",
                        proposedCompletionDate: proposedCompletionDate || undefined,
                      })
                    )
                  }
                >
                  Accept offer
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={busy === "decline"}
                  onClick={() => {
                    const reason = window.prompt("Reason for declining this offer?", "");
                    if (reason === null) return;
                    runAction("decline", () =>
                      respondNetworkPortalOffer(offer.id, {
                        responseType: "decline",
                        declineReason: reason,
                      })
                    );
                  }}
                >
                  Decline offer
                </Button>
                <Button
                  variant="outlined"
                  disabled={busy === "counter"}
                  onClick={() =>
                    runAction("counter", () =>
                      respondNetworkPortalOffer(offer.id, {
                        responseType: "counter",
                        responseNote: responseNote || undefined,
                        question: question || undefined,
                        proposedFeeAmount: proposedFeeAmount || undefined,
                        proposedFeeCurrency: offer?.currency || "CAD",
                        proposedCompletionDate: proposedCompletionDate || undefined,
                      })
                    )
                  }
                >
                  Propose update
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                <TextField
                  label="Proposed fee"
                  value={proposedFeeAmount}
                  onChange={(event) => setProposedFeeAmount(event.target.value)}
                  placeholder={offer?.quotedPrice ? String(offer.quotedPrice) : ""}
                />
                <TextField
                  type="date"
                  label="Proposed completion date"
                  value={proposedCompletionDate}
                  onChange={(event) => setProposedCompletionDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  multiline
                  minRows={3}
                  label="Talent note"
                  value={responseNote}
                  onChange={(event) => setResponseNote(event.target.value)}
                />
                <TextField
                  multiline
                  minRows={2}
                  label="Ask a question"
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: "1px solid #d9e2ec", borderRadius: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Source Files</Typography>
              {safeArray(workRequest?.filesJson).length ? safeArray(workRequest.filesJson).map((file, index) => (
                <Typography key={`${file.url || file.fileName || "file"}-${index}`} variant="body2">{file.url || file.fileName || JSON.stringify(file)}</Typography>
              )) : (
                <Typography variant="body2" color="text.secondary">No source files were shared.</Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ border: "1px solid #d9e2ec", borderRadius: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Messages</Typography>
              {unreadMessageCount ? <Alert severity="info">{unreadMessageCount} unread message{unreadMessageCount === 1 ? "" : "s"}.</Alert> : null}
              {messages.length ? messages.map((message) => (
                <Card key={message.id} elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf2f7" }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <StatusChip value={message.senderType} />
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{message.senderDisplayName || "Sender"}</Typography>
                      {!message.isRead ? <StatusChip value="unread" /> : null}
                      <Typography variant="caption" color="text.secondary">{formatDateTime(message.createdAt)}</Typography>
                    </Stack>
                    {message.messageBody ? <Typography variant="body2">{message.messageBody}</Typography> : null}
                    {safeArray(message.attachmentsJson).length ? safeArray(message.attachmentsJson).map((attachment) => (
                      <Button
                        key={attachment.fileId || attachment.fileName}
                        variant="text"
                        size="small"
                        sx={{ justifyContent: "flex-start", textTransform: "none", p: 0, minWidth: 0 }}
                        onClick={() => openMessageAttachment(message, attachment)}
                      >
                        {attachment.fileName || "Attachment"}
                      </Button>
                    )) : null}
                  </Stack>
                </Card>
              )) : (
                <Typography variant="body2" color="text.secondary">No messages yet.</Typography>
              )}
              <Button variant="outlined" component="label" disabled={busy === "message-upload" || busy === "message"}>
                Select message attachments
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.ai,.eps,.ps,.svg,.png,.jpg,.jpeg,.webp,.gif,.zip"
                  onChange={(event) => setMessageSelectedFiles(Array.from(event.target.files || []))}
                />
              </Button>
              {messageSelectedFiles.length ? (
                <Stack spacing={0.5}>
                  {messageSelectedFiles.map((file) => (
                    <Typography key={`${file.name}-${file.size}`} variant="caption" color="text.secondary">
                      {file.name}
                    </Typography>
                  ))}
                  <div>
                    <Button
                      variant="text"
                      disabled={busy === "message-upload"}
                      onClick={() => runAction("message-upload", async () => {
                        const response = await uploadNetworkPortalMessageAttachments(workRequest.id, messageSelectedFiles);
                        setUploadedMessageFiles(safeArray(response.files));
                        setMessageSelectedFiles([]);
                      })}
                    >
                      Upload attachments
                    </Button>
                  </div>
                </Stack>
              ) : null}
              {uploadedMessageFiles.length ? uploadedMessageFiles.map((file) => (
                <Typography key={file.fileId} variant="caption" color="text.secondary">
                  Attached: {file.fileName}
                </Typography>
              )) : null}
              <textarea
                value={messageBody}
                onChange={(event) => setMessageBody(event.target.value)}
                rows={4}
                style={{ width: "100%", padding: 12, borderRadius: 12, borderColor: "#d9e2ec", fontFamily: "inherit" }}
              />
              <Button
                variant="contained"
                disabled={busy === "message" || (!messageBody.trim() && !uploadedMessageFiles.length)}
                onClick={() => runAction("message", async () => {
                  await addNetworkPortalMessage(workRequest.id, {
                    messageBody,
                    attachmentsJson: uploadedMessageFiles,
                  });
                  setMessageBody("");
                  setUploadedMessageFiles([]);
                })}
              >
                Send message
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {submissions.length ? (
          <Card elevation={0} sx={{ border: "1px solid #d9e2ec", borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Existing submissions</Typography>
                {submissions.map((submission) => (
                  <Card key={submission.id} elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf2f7" }}>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StatusChip value={submission.status} />
                        <Typography variant="caption" color="text.secondary">{`v${Number(submission.version || 1)}`}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDateTime(submission.submittedAt)}</Typography>
                      </Stack>
                      <Typography variant="body2">{submission.notes || "No notes."}</Typography>
                      {submission.reviewNotes ? (
                        <Typography variant="caption" color="text.secondary">
                          Review: {submission.reviewNotes}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ) : null}
      </Stack>
    </NetworkPortalLayout>
  );
}
